import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import AceEditor from 'react-ace';

import { Code } from '@phosphor-icons/react';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/theme-monokai';
import {
  IChatbotFlowModel,
  IPublicChatbotModel,
} from '../../data/models/chatbot';

interface ChatbotCodeComponentProps {
  className?: string;
  children?: any;
  chatbot: IChatbotFlowModel | IPublicChatbotModel;
  onOpenFlyout?: (code: string, language: string, timestamp?: string) => void;
  messageTimestamp?: string;
}

export function ChatbotCodeComponent({
  className,
  children,
  onOpenFlyout,
  chatbot,
  messageTimestamp,
  ...props
}: ChatbotCodeComponentProps) {
  const match = /language-(\w+)/.exec(className ?? '');
  const isHtml = match?.[1] === 'html';

  const [isPreview, setIsPreview] = useState(() => isHtml ? true : false);

  if (!match) {
    return (
      <code className="bg-gray-200 text-sm px-1 py-0.5 rounded">
        {children}
      </code>
    );
  }

  const language = match?.[1] ?? 'text';

  const codeContent = String(children).replace(/\n$/, '');

  const handleOpenInFlyout = () => {
    if (onOpenFlyout) {
      onOpenFlyout(codeContent, language, messageTimestamp);
    }
  };

  const shouldShowInline =
    chatbot.code &&
    chatbot.enableChatFlyout &&
    chatbot.contentSource !== 'output-node';

  if (shouldShowInline) {
    return (
      <Box
        onClick={handleOpenInFlyout}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: 'transparent',
          border: `1px solid ${
            chatbot.brandColor ? chatbot.brandColor : '#3650DE'
          }`,
          borderRadius: '16px',
          gap: '12px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
          width: 'fit-content',
        }}
      >
        <Code
          size={16}
          color={chatbot.brandColor ? chatbot.brandColor : '#3650DE'}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="body1"
            fontSize={14}
            color={chatbot.responseText ? chatbot.responseText : '#3650DE'}
          >
            {language.toUpperCase()} CODE
          </Typography>

          <Typography
            variant="body1"
            fontSize={12}
            color={chatbot.pageText ? chatbot.pageText : '#fff'}
          >
            Interactive artifact
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isHtml && (
        <Button
          size="small"
          onClick={() => setIsPreview(!isPreview)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.2)',
            },
          }}
        >
          {isPreview ? 'Raw' : 'Preview'}
        </Button>
      )}

      {isPreview ? (
        <Box sx={{ width: '100%', minHeight: '400px' }}>
          <iframe
            srcDoc={String(children).replace(/\n$/, '')}
            title="Code Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{
              minHeight: 'inherit',
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          />
        </Box>
      ) : (
        <AceEditor
          mode="javascript"
          theme="monokai"
          name="chatbot-code"
          value={children}
          fontSize={14}
          style={{
            borderRadius: '8px',
            width: '100%',
          }}
          lineHeight={19}
          showGutter={true}
          highlightActiveLine={true}
          readOnly
          setOptions={{
            useWorker: false,
          }}
        />
      )}
    </Box>
  );
}
